import {
	apiCheckOrganizerFeatureAccess,
	apiLoadOrganizerFeatureAccessMenu,
} from '@/apis/bridge/OrganizerFeatureAccessBridge';
import {
	organizerFeatureAccessCheckResponseModel,
	organizerFeatureAccessFeatureModel,
	organizerFeatureAccessMenuResponseModel,
} from '@/models/organizer-feature-access';

export const ORGANIZER_FEATURE_ACCESS_PROGRAM = 'organizer-ems';
export const FEATURE_NOT_ENABLED_CODE = 'FEATURE_NOT_ENABLED';

const getDefaultState = () => ({
	program: ORGANIZER_FEATURE_ACCESS_PROGRAM,
	ogzuid: '',
	features: [],
	enabledMap: {},
	catalogLoaded: false,
	loading: false,
	error: null,
});

const getResponseData = response => {
	const data = response?.data;
	if (typeof data !== 'string') return data || {};

	try {
		return JSON.parse(data);
	} catch (error) {
		return {};
	}
};

const getErrorInfo = error => ({
	status: error?.response?.status || 0,
	code: error?.response?.data?.code || '',
});

const isValidMenuResponse = data => {
	const model = organizerFeatureAccessMenuResponseModel();
	return (
		data?.status === 'OK' &&
		data?.program === model.program &&
		Array.isArray(data?.features)
	);
};

const normalizeFeatures = features => {
	const enabledMap = {};
	const normalized = [];

	(features || []).forEach(feature => {
		const model = organizerFeatureAccessFeatureModel();
		if (
			typeof feature?.funcid !== 'string' ||
			!feature.funcid ||
			typeof feature.enabled !== 'boolean'
		) {
			return;
		}

		const normalizedFeature = {
			...model,
			funcid: feature.funcid,
			enabled: feature.enabled,
		};
		normalized.push(normalizedFeature);
		enabledMap[normalizedFeature.funcid] = normalizedFeature.enabled;
	});

	return { normalized, enabledMap };
};

const FeatureAccessModule = {
	namespaced: true,
	state: getDefaultState(),
	getters: {
		isEnabled: state => featureKey =>
			state.catalogLoaded && state.enabledMap[featureKey] === true,
		getEnabledMap: state => state.enabledMap,
	},
	mutations: {
		beginLoad(state, ogzuid) {
			state.loading = true;
			state.error = null;
			if (state.ogzuid !== ogzuid) {
				state.ogzuid = ogzuid || '';
				state.features = [];
				state.enabledMap = {};
				state.catalogLoaded = false;
			}
		},
		setMenuAccess(state, { ogzuid, data }) {
			const { normalized, enabledMap } = normalizeFeatures(data.features);
			state.program = data.program;
			state.ogzuid = ogzuid || '';
			state.features = normalized;
			state.enabledMap = enabledMap;
			// 空目錄或欄位不完整時維持 fail closed，不把它視為有效授權目錄。
			state.catalogLoaded = normalized.length > 0;
			state.loading = false;
			state.error = null;
		},
		setFailure(state, error) {
			state.features = [];
			state.enabledMap = {};
			state.catalogLoaded = false;
			state.loading = false;
			state.error = getErrorInfo(error);
		},
		reset(state) {
			Object.assign(state, getDefaultState());
		},
	},
	actions: {
		async loadMenu({ commit }, { ogzuid }) {
			commit('beginLoad', ogzuid);

			try {
				const response = await apiLoadOrganizerFeatureAccessMenu(
					ogzuid,
					{}
				);
				const data = getResponseData(response);

				if (!isValidMenuResponse(data)) {
					throw new Error('Invalid organizer feature-access menu response');
				}

				commit('setMenuAccess', { ogzuid, data });
				return true;
			} catch (error) {
				commit('setFailure', error);
				throw error;
			}
		},
		async checkFeature(_context, { ogzuid, featureKey }) {
			if (!ogzuid || !featureKey) return false;

			try {
				const response = await apiCheckOrganizerFeatureAccess(
					ogzuid,
					featureKey,
					{}
				);
				const data = getResponseData(response);
				const model = organizerFeatureAccessCheckResponseModel();

				return (
					data?.status === 'OK' &&
					data?.program === model.program &&
					data?.funcid === featureKey &&
					data?.enabled === true
				);
			} catch (error) {
				// Page denial is an authorization result, not a login-expiry result.
				if (error?.response?.status === 403) return false;
				throw error;
			}
		},
	},
};

export default FeatureAccessModule;

let featureAccessBootstrapReady = false;
let resolveFeatureAccessBootstrap;
const featureAccessBootstrapPromise = new Promise(resolve => {
	resolveFeatureAccessBootstrap = resolve;
});

export const waitForFeatureAccessBootstrap = () =>
	featureAccessBootstrapReady
		? Promise.resolve()
		: featureAccessBootstrapPromise;

export const markFeatureAccessBootstrapReady = () => {
	if (featureAccessBootstrapReady) return;
	featureAccessBootstrapReady = true;
	resolveFeatureAccessBootstrap();
};
